"use client";
import * as NavMenu from "@radix-ui/react-navigation-menu";
import clsx from "clsx";
import Link from "next/link";

import getLink from "@/lib/storyblok/utils/getLink";
import type {
  LinkStoryblok,
  MenuSectionStoryblok,
  MenuStoryblok,
} from "@/types/storyblok-blok-types";

import { Back } from "@/components/icons/Back";
import ChevronLeft from "@/components/icons/ChevronLeft";
import { useEffect, useState } from "react";
import styles from "./header-bloks.module.css";
import { useCuratedTabs } from "./hooks";
import { SectionGrid } from "./SectionGrid";
import { SectionTabs } from "./SectionTabs";
import { usePathname } from "next/navigation";

const UI = ({
  menu,
  link,
  index,
  topLinks,
  allMenus,
  value,
}: {
  menu: MenuStoryblok;
  link?: string | undefined;
  index: number;
  topLinks?: LinkStoryblok[];
  allMenus?: MenuStoryblok[];
  value?: string;
}) => {
  const pathname = usePathname();
  const [activeSubMenu, setActiveSubMenu] = useState<MenuStoryblok | null>(
    null
  );

  const [activeSubSection, setActiveSubSection] =
    useState<MenuSectionStoryblok | null>(null);
  useEffect(() => {
    setActiveSubMenu(null);
    setActiveSubSection(null);
  }, [pathname]);
  const isCurated = menu.isCurated;
  const isTopLevel = index === 0;

  const effectiveIsCurated =
    !isTopLevel && (activeSubMenu?.isCurated || menu.isCurated);
  const {
    activeUid: activeCuratedSectionUid,
    setActiveUid: setActiveCuratedSectionUid,
  } = useCuratedTabs(menu);

  const {
    activeUid: activeCuratedSubmenuSectionUid,
    setActiveUid: setActiveCuratedSubmenuSectionUid,
  } = useCuratedTabs(activeSubMenu);

  return (
    <NavMenu.Item value={value}>
      {link ? (
        <NavMenu.Trigger className={clsx(styles.menuButton)}>
          <Link
            href={link}
            className={styles.menu__button__link}
            tabIndex={-1}
            onKeyDownCapture={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          >
            {menu.title}
            <div className={styles.underline} />
          </Link>
        </NavMenu.Trigger>
      ) : (
        <NavMenu.Trigger className={clsx(styles.menuButton)}>
          {menu.title}
          <div className={styles.underline} />
        </NavMenu.Trigger>
      )}
      {index === 0 ? (
        <NavMenu.Content
          className={clsx(
            styles.menuContent,
            activeSubMenu?.isDenim && styles.viewportPihlDenim
          )}
        >
          <div className={styles.menuPanel}>
            {!activeSubMenu ? (
              <div className={styles.menu__page}>
                <section className={styles.top__menu__links}>
                  <div className={styles.top__links}>
                    {/* Top links */}
                    {topLinks?.map((link) => {
                      const href = getLink(link.link);
                      return (
                        <p key={link._uid} className={styles.menu__item__link}>
                          <Link href={href ?? "#"}>{link.text}</Link>
                        </p>
                      );
                    })}
                  </div>

                  {menu.section
                    ?.filter((section) => section.showOnMobile)
                    .map((section) => (
                      <button
                        key={`section-${section._uid}`}
                        className={styles.menu__item}
                        onClick={() => {
                          setActiveSubMenu(menu);
                          setActiveSubSection(section);
                        }}
                      >
                        <span className={styles.menu__index__title}>
                          {section.title}
                        </span>
                        <ChevronLeft />
                      </button>
                    ))}

                  {allMenus
                    ?.filter((m) => m.title !== menu.title)
                    .map((menuItem) => (
                      <button
                        key={`menu-${menuItem._uid}`}
                        className={styles.menu__item}
                        onClick={() => {
                          setActiveSubMenu(menuItem);
                          setActiveSubSection(null);
                        }}
                      >
                        <span className={styles.menu__index__title}>
                          {menuItem.title}
                        </span>
                        <ChevronLeft />
                      </button>
                    ))}
                </section>
              </div>
            ) : (
              <div className={styles.menu__slide}>
                <div className={styles.scrollArea}>
                  <nav className={styles.menu__topbar}>
                    <button
                      className={styles.back__control}
                      onClick={() => {
                        setActiveSubMenu(null);
                        setActiveSubSection(null);
                      }}
                    >
                      <Back />
                      <span className={styles.back__button}>Back</span>
                    </button>
                  </nav>

                  <h2 className={styles.menu__title}>
                    {activeSubSection?.title || activeSubMenu.title}
                  </h2>
                  {activeSubMenu?.isCurated && (
                    <SectionTabs
                      sections={activeSubMenu.section ?? []}
                      activeUid={activeCuratedSubmenuSectionUid}
                      setActiveUid={setActiveCuratedSubmenuSectionUid}
                    />
                  )}

                  {activeSubMenu?.isCurated ? (
                    activeSubMenu.section
                      ?.filter(
                        (s) =>
                          s._uid === activeCuratedSubmenuSectionUid &&
                          s?.showOnMobile
                      )
                      .map((section) => (
                        <SectionGrid
                          key={section._uid}
                          section={section}
                          isCurated={effectiveIsCurated}
                        />
                      ))
                  ) : activeSubSection ? (
                    <SectionGrid
                      key={activeSubSection._uid}
                      section={activeSubSection}
                      isDenim={activeSubMenu?.isDenim}
                    />
                  ) : (
                    activeSubMenu?.section
                      ?.filter((s) => s?.showOnMobile)
                      .map((section) => (
                        <SectionGrid
                          key={section._uid}
                          section={section}
                          isDenim={activeSubMenu?.isDenim}
                        />
                      ))
                  )}
                </div>
              </div>
            )}
          </div>
        </NavMenu.Content>
      ) : (
        // Normal fallback for other menus
        <NavMenu.Content
          className={clsx(
            styles.menuContent,
            styles.menu__page,
            menu.isDenim && styles.viewportPihlDenim,
            isCurated && styles.viewportFullWidth
          )}
        >
          <div className={styles.scrollArea}>
            <div className={styles.menuPanel}>
              <h2 className={styles.menu__title}>{menu.title}</h2>
              {isCurated && (
                <SectionTabs
                  sections={menu.section ?? []}
                  activeUid={activeCuratedSectionUid}
                  setActiveUid={setActiveCuratedSectionUid}
                />
              )}

              {isCurated
                ? menu.section
                    ?.filter(
                      (section) => section._uid === activeCuratedSectionUid
                    )
                    .map((section) => (
                      <SectionGrid
                        key={section._uid}
                        section={section}
                        isCurated={isCurated}
                      />
                    ))
                : menu.section?.map((section) => (
                    <SectionGrid
                      key={section._uid}
                      section={section}
                      isDenim={menu.isDenim}
                    />
                  ))}
            </div>
          </div>
        </NavMenu.Content>
      )}
    </NavMenu.Item>
  );
};

type MenuItemProps = {
  menu: MenuStoryblok;
  index: number;
  topLinks?: LinkStoryblok[];
  allMenus?: MenuStoryblok[];
  value?: string;
};

const MenuItem = ({
  menu,
  index,
  topLinks,
  allMenus,
  value,
}: MenuItemProps) => {
  const link = getLink(menu?.link);
  return (
    <UI
      menu={menu}
      link={link}
      index={index}
      topLinks={topLinks}
      allMenus={allMenus}
      value={value}
    />
  );
};

export default MenuItem;
